/**
 * รายละเอียดคำขอเอกสารบริษัท (หน้าเต็ม — house style) — อนุมัติ/ปฏิเสธ + ตัวเลือกการปั๊ม
 */
import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useToast } from '../../components/ui/toast';
import { useConsequence } from '../../components/ui/ConsequencePreview';
import { useCompanyDocRequestDetail, useDecideCompanyDocRequest, type StampOptions } from '../../hooks';
import { ArrowLeft, Download, CheckCircle2, XCircle } from 'lucide-react';
import { LoadError } from '../../components/layout/LoadError';

const STATUS_LABEL: Record<string, string> = {
  pending: 'รออนุมัติ',
  approved: 'อนุมัติแล้ว',
  rejected: 'ปฏิเสธ',
  canceled: 'ยกเลิก',
};

const DEFAULT_STAMP_OPTIONS: Required<StampOptions> = { cross: true, cpos: 40, stamp: true, spos: 'all', cert: true };

export function OwnerCompanyDocRequestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, isLoading, error, refetch } = useCompanyDocRequestDetail(id ?? null);
  const decideM = useDecideCompanyDocRequest();
  const consequence = useConsequence();
  const toast = useToast();

  const [cpos, setCpos] = useState<22 | 40 | 60>(DEFAULT_STAMP_OPTIONS.cpos);
  const [spos, setSpos] = useState<'all' | 'first' | 'last'>(DEFAULT_STAMP_OPTIONS.spos);
  const [stamp, setStamp] = useState(true);
  const [cert, setCert] = useState(true);
  const [rejectNote, setRejectNote] = useState('');
  const [showReject, setShowReject] = useState(false);

  const req = data?.request;

  async function handleApprove() {
    const ok = await consequence({
      title: 'อนุมัติคำขอเอกสาร',
      intro: 'เมื่อกดยืนยัน ระบบจะดำเนินการต่อไปนี้',
      effects: ['ระบบจะออกสำเนาประทับตราส่งให้พนักงานทันที', 'แจ้งพนักงานทาง LINE ว่าอนุมัติแล้ว'],
      confirmText: 'อนุมัติ',
    });
    if (!ok) return;
    try {
      await decideM.mutateAsync({ id: id!, action: 'approve', stamp_options: { cross: true, cpos, stamp, spos, cert } });
      toast.success('อนุมัติคำขอแล้ว กำลังออกสำเนา');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'อนุมัติไม่สำเร็จ');
    }
  }

  async function handleReject() {
    if (!rejectNote.trim()) { toast.error('กรุณาระบุเหตุผลที่ปฏิเสธ'); return; }
    try {
      await decideM.mutateAsync({ id: id!, action: 'reject', note: rejectNote.trim() });
      toast.success('ปฏิเสธคำขอแล้ว');
      setShowReject(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ปฏิเสธไม่สำเร็จ');
    }
  }

  return (
    <AdminLayout title="รายละเอียดคำขอเอกสาร">
      <button onClick={() => navigate('/owner/company-docs#requests')} className="text-sm text-gray-500 flex items-center gap-1 mb-3">
        <ArrowLeft className="w-4 h-4" /> กลับไปรายการคำขอ
      </button>

      {error ? (
        <LoadError error={error} onRetry={() => refetch()} />
      ) : isLoading || !req ? (
        <p className="text-sm text-gray-400">กำลังโหลด…</p>
      ) : (
        <div className="space-y-4">
          <div className="card p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="font-mono text-xs text-gray-400">{req.req_no}</div>
              <span className="whitespace-nowrap text-xs font-medium rounded-full px-2.5 py-1 bg-gray-100 text-gray-700">
                {STATUS_LABEL[req.status] ?? req.status}
              </span>
            </div>
            <table className="w-full text-sm">
              <tbody className="divide-y divide-gray-100">
                <tr><td className="py-1.5 text-gray-500">ผู้ขอ</td><td className="py-1.5 text-right font-medium">{req.emp_name ?? req.emp_code}</td></tr>
                <tr><td className="py-1.5 text-gray-500">วัตถุประสงค์</td><td className="py-1.5 text-right">{req.purpose}</td></tr>
                {req.recipient && <tr><td className="py-1.5 text-gray-500">ส่งให้</td><td className="py-1.5 text-right">{req.recipient}</td></tr>}
                <tr><td className="py-1.5 text-gray-500">ยื่นเมื่อ</td><td className="py-1.5 text-right">{new Date(req.created_at).toLocaleString('th-TH')}</td></tr>
                {req.decision_note && <tr><td className="py-1.5 text-gray-500">เหตุผล</td><td className="py-1.5 text-right">{req.decision_note}</td></tr>}
              </tbody>
            </table>
          </div>

          <div className="card p-4">
            <div className="font-semibold mb-2 text-sm">เอกสารที่ขอ ({req.docs.length})</div>
            <ul className="space-y-1">
              {req.docs.map((d) => (
                <li key={d.id} className="text-sm text-gray-700 flex gap-2">
                  <span className="text-gray-400">•</span> {d.label}
                </li>
              ))}
            </ul>
          </div>

          {req.status === 'pending' && (
            <div className="card p-4 space-y-3">
              <div className="font-semibold text-sm">ตัวเลือกการปั๊มสำเนา</div>
              <div>
                <label className="label">ตำแหน่งเส้นคาด</label>
                <div className="flex gap-2">
                  {([22, 40, 60] as const).map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setCpos(v)}
                      className={`flex-1 rounded-lg border-2 py-2 text-sm ${cpos === v ? 'border-brand-500 bg-brand-50 font-semibold' : 'border-gray-200'}`}
                    >
                      {v}%
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="label">ตำแหน่งตราประทับ</label>
                <select value={spos} onChange={(e) => setSpos(e.target.value as 'all' | 'first' | 'last')} className="input w-full">
                  <option value="all">ทุกหน้า</option>
                  <option value="first">หน้าแรกของแต่ละเอกสาร</option>
                  <option value="last">หน้าสุดท้ายของแต่ละเอกสาร</option>
                </select>
              </div>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={stamp} onChange={(e) => setStamp(e.target.checked)} /> ประทับตรา
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={cert} onChange={(e) => setCert(e.target.checked)} /> บล็อกสำเนาถูกต้อง
                </label>
              </div>

              <div className="flex gap-2 pt-2">
                <button onClick={() => setShowReject(true)} disabled={decideM.isPending} className="btn-secondary flex-1">
                  <XCircle className="w-4 h-4" /> ปฏิเสธ
                </button>
                <button onClick={handleApprove} disabled={decideM.isPending} className="btn-cta flex-1">
                  <CheckCircle2 className="w-4 h-4" /> {decideM.isPending ? 'กำลังอนุมัติ…' : 'อนุมัติ'}
                </button>
              </div>

              {showReject && (
                <div className="border-t border-gray-100 pt-3 space-y-2">
                  <label className="label">เหตุผลที่ปฏิเสธ *</label>
                  <textarea value={rejectNote} onChange={(e) => setRejectNote(e.target.value)} rows={2} className="input w-full" />
                  <button onClick={handleReject} disabled={decideM.isPending} className="btn-danger w-full">
                    ยืนยันปฏิเสธ
                  </button>
                </div>
              )}
            </div>
          )}

          {req.status === 'approved' && req.stamped_download_url && (
            <a href={req.stamped_download_url} target="_blank" rel="noreferrer" className="btn-cta w-full flex items-center justify-center gap-2">
              <Download className="w-4 h-4" /> ดาวน์โหลดสำเนาที่ประทับตราแล้ว
            </a>
          )}
        </div>
      )}
    </AdminLayout>
  );
}
